import { useRef, useState } from 'react';
import { AVATAR_ACCEPTED_MIME_TYPES, AVATAR_MAX_BYTES } from '@supporchat/shared';
import { api } from '../lib/api';
import { Avatar } from './Avatar';

/**
 * A foto do atendente que está usando o painel.
 *
 * Fica ao lado da assinatura porque as duas respondem à mesma pergunta — quem
 * está atendendo —, cada uma para um lado: a assinatura é o que o cliente lê no
 * WhatsApp, a foto é o que os colegas veem no painel. Ela **não** vai para o
 * WhatsApp: o número da empresa tem uma foto só, e trocá-la por atendente mudaria
 * o retrato da empresa a cada mensagem.
 *
 * Cada um mexe apenas na própria foto: o servidor tira o usuário do token e ignora
 * qualquer id que venha no pedido.
 */
export function ProfilePhotoCard({
  userId,
  userName,
  version,
  onChange,
}: {
  userId: string;
  userName: string;
  /** `avatarUpdatedAt` em vigor; `null` quando não há foto. */
  version: string | null;
  onChange: (version: string | null) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const escolher = async (file: File | undefined) => {
    if (!file) return;

    setErro(null);

    // Checagem no navegador para o erro aparecer antes de subir megabytes por uma
    // conexão ruim. Quem decide de verdade é o servidor, olhando os bytes.
    if (file.size > AVATAR_MAX_BYTES) {
      setErro(
        `A foto tem ${Math.round(file.size / 1024)} kB e o limite é ${Math.round(AVATAR_MAX_BYTES / 1024)} kB. Escolha uma imagem menor.`,
      );
      return;
    }

    setEnviando(true);
    try {
      const { avatarUpdatedAt } = await api.uploadMyAvatar(file);
      onChange(avatarUpdatedAt);
    } catch (err) {
      setErro((err as Error).message);
    } finally {
      setEnviando(false);
      // Sem isto, escolher o mesmo arquivo depois de um erro não dispararia o
      // `change` do input — e a tentativa de novo parecia não funcionar.
      if (input.current) input.current.value = '';
    }
  };

  const remover = async () => {
    setErro(null);
    setEnviando(true);
    try {
      await api.removeMyAvatar();
      onChange(null);
    } catch (err) {
      setErro((err as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="card">
      <h3 className="card__title">Sua foto</h3>
      <p className="card__subtitle">
        Aparece no painel, ao lado das mensagens que você escreve — é como o time enxerga quem falou
        com o cliente. O cliente continua vendo a foto do número da empresa no WhatsApp.
      </p>

      {erro && <div className="alert alert--error">{erro}</div>}

      <div className="profile-photo">
        <Avatar kind="user" id={userId} name={userName} version={version} size="lg" />

        <div className="profile-photo__actions">
          <input
            ref={input}
            id="profile-photo-file"
            className="visually-hidden"
            type="file"
            accept={AVATAR_ACCEPTED_MIME_TYPES.join(',')}
            disabled={enviando}
            onChange={(event) => void escolher(event.target.files?.[0])}
          />
          <label className="btn btn--sm" htmlFor="profile-photo-file">
            {enviando ? 'Enviando' : version ? 'Trocar foto' : 'Escolher foto'}
          </label>
          {version && (
            <button className="btn btn--sm" disabled={enviando} onClick={() => void remover()}>
              Remover
            </button>
          )}
          <div className="field__hint">
            JPEG, PNG ou WebP, até {Math.round(AVATAR_MAX_BYTES / (1024 * 1024))} MB. Sem foto, o painel
            mostra suas iniciais.
          </div>
        </div>
      </div>
    </div>
  );
}
